"use client";

import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { useEffect, useRef } from "react";

// A headline figure that counts up when it scrolls into view.
//
// The figures on this site are part of the copy — they live in the messages
// files as whole strings ("100+", "4+", "100/100") so each locale can format
// them itself. So rather than take a number, this takes the already-translated
// string and animates the FIRST run of digits in it, leaving everything around
// that run alone:
//
//   "100+"      → 0+   … 100+
//   "4+"        → 0+   … 4+
//   "100/100"   → 0/100 … 100/100
//
// The real string is always in the markup as screen-reader text, so the value is
// present from the first server render — crawlers and assistive tech never see
// the counter's intermediate states, and there is nothing for hydration to
// disagree about (both server and client start the visible span at zero).
const NUMERIC = /^([^0-9]*)([0-9][0-9,]*)(.*)$/s;

export default function CountUpValue({
  value,
  count = true,
  delay = 0,
  duration = 1.2,
  className = "",
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const reduce = useReducedMotion();

  const match = typeof value === "string" ? value.match(NUMERIC) : null;
  const digits = match ? match[2] : "";
  const grouped = digits.includes(",");
  const target = digits ? Number(digits.replace(/,/g, "")) : 0;

  const progress = useMotionValue(0);
  const text = useTransform(progress, (v) => {
    const n = Math.round(v);
    return grouped ? n.toLocaleString("en-US") : String(n);
  });

  const counting = count && match !== null;

  useEffect(() => {
    if (!counting || !inView) return;

    // Reduced motion still gets the number, just not the climb. This only
    // changes an effect, never which elements render — branching the DOM on
    // useReducedMotion() tears the tree apart on hydration.
    if (reduce) {
      progress.set(target);
      return;
    }

    const controls = animate(progress, target, {
      duration,
      delay,
      ease: "easeOut",
    });
    return () => controls.stop();
  }, [counting, inView, reduce, progress, target, duration, delay]);

  // No digits to count, or the caller opted out (a figure like "1K+" has only a
  // single digit to climb, so counting it reads as a glitch rather than a
  // tally). Fade it in on the same beat instead.
  if (!counting) {
    return (
      <motion.span
        ref={ref}
        className={className}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.4, delay }}
      >
        {value}
      </motion.span>
    );
  }

  const [, prefix, , suffix] = match;

  return (
    // tabular-nums so the figure holds its width while the digits change —
    // otherwise the copy beside it jitters for the length of the count.
    <span ref={ref} className={`tabular-nums ${className}`}>
      <span className="sr-only">{value}</span>
      <span aria-hidden="true">
        {prefix}
        <motion.span>{text}</motion.span>
        {suffix}
      </span>
    </span>
  );
}
